import {
  CssBaseline,
  Card,
  Typography,
  Box,
  FormControl,
  FormLabel,
  TextField,
  Button,
  Stack,
  Alert,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { login, setUserNavigation } from '../../store/Reducers/UserSlice';
import { useNavigate } from 'react-router-dom';
import { CHANGE_PASSWORD_ROUTE, DASHBOARD_ROUTE } from '../../constants/routes';

import { useForm } from 'react-hook-form';
import { ILoginPayload } from '../../types/api/request/authrequest';
import { AuthService } from '../../api/services/authservice';
import { SessionService } from '../../api/services/sessionservice';
import { setRolesAndPermissions } from '../../store/Reducers/RolesAndPermissionsSlice';
import { setPermissions } from '../../store/Reducers/PermissionsSlice';
import { setAdminUsers } from '../../store/Reducers/AdminUsersSlice';
import { useEffect, useState } from 'react';
import { IRootState } from '../../types/store';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { setNavigationPages } from '../../store/Reducers/NavigationSlice';

/* =======================
   Styled Components
======================= */

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles?.('dark', {
      backgroundImage: 'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

/* =======================
   Component
======================= */

export const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showForgotPasswordHint, setShowForgotPasswordHint] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ILoginPayload>({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: ILoginPayload) => {
    try {
      const authenticatedUser = await AuthService.login(data);
      console.log('authenticated user', authenticatedUser.token);
      // Save token centrally
      localStorage.setItem('ss_adm_pnl_access_token', authenticatedUser.token);
      localStorage.setItem('ss_adm_pnl_user', JSON.stringify(authenticatedUser.user));
      dispatch(
        login({
          user: authenticatedUser.user,
        }),
      );
      console.log(
        'authenticatedUser.user.password_expired',
        authenticatedUser.user.password_expired,
      );

      if (!authenticatedUser.user.password_expired) {
        console.log('iamhere');
        const init = await SessionService.init(
          authenticatedUser.user.roles.map((role) => role.name),
        );

        localStorage.setItem(
          'ss_adm_pnl_menu_items',
          JSON.stringify(init.navigationItems.navigation),
        );
        dispatch(setUserNavigation(init.navigationItems.navigation));

        if (init.rolesAndPermissions) {
          dispatch(setRolesAndPermissions(init.rolesAndPermissions));
        }

        if (init.permissions) {
          dispatch(setPermissions(init.permissions));
        }

        if (init.adminUsers) {
          dispatch(setAdminUsers(init.adminUsers));
        }
      }
    } catch (err: any) {
      console.error(err?.message);
      dispatch(
        showNotification({
          message: `Login failed. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const isLoggedIn = useSelector((state: IRootState) => state.user.isLoggedIn);

  useEffect(() => {
    if (isLoggedIn) {
      navigate(DASHBOARD_ROUTE, { replace: true });
    }
  }, [isLoggedIn, navigate]);

  return (
    <>
      <CssBaseline enableColorScheme />

      <SignInContainer direction="column" justifyContent="center" sx={{ alignItems: 'center' }}>
        <Card
          variant="outlined"
          sx={{
            minWidth: '200px',
            maxWidth: '500px',
            width: '100%',
            padding: '20px',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{
              width: '100%',
              fontSize: 'clamp(2rem, 10vw, 2.15rem)',
              textAlign: 'center',
              paddingBottom: '20px',
            }}
          >
            Welcome, Admin!
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            {/* Username */}
            <FormControl>
              <FormLabel>Username</FormLabel>
              <TextField
                type="text"
                placeholder="your@email.com"
                fullWidth
                variant="outlined"
                error={!!errors.username}
                helperText={errors.username?.message}
                {...register('username', {
                  required: 'Username is required',
                })}
              />
            </FormControl>

            {/* Password */}
            <FormControl>
              <FormLabel>Password</FormLabel>
              <TextField
                type="password"
                placeholder="••••••"
                fullWidth
                variant="outlined"
                error={!!errors.password}
                helperText={errors.password?.message}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Minimum 6 characters',
                  },
                })}
              />
            </FormControl>

            <Box display="flex" justifyContent="flex-end">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowForgotPasswordHint((prev) => !prev)}
              >
                Forgot password?
              </Link>
            </Box>

            {showForgotPasswordHint && (
              <Alert severity="info" onClose={() => setShowForgotPasswordHint(false)}>
                Forgot your password? Contact your superadmin — they can generate a temporary
                password for you from the admin panel.
              </Alert>
            )}

            {/* Submit */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ backgroundColor: '#000' }}
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </Box>
        </Card>
      </SignInContainer>
    </>
  );
};
